import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { DataCard, DataCardActions, DataCardEmpty, DataCardRow } from '../../components/ui/DataCard';
import { TextField } from '../../components/forms/TextField';
import { ImageUpload, type PendingFile } from '../../components/forms/ImageUpload';
import { RichTextEditor } from '../../components/forms/RichTextEditor';
import { cn } from '../../utils/cn';
import { usersStore, type UserRecord } from './UsersStore';
import { filesApi } from '../../api/FilesApi';
import { copyToClipboard } from '../../utils/clipboard';
import { useI18n } from '../../i18n/I18nContext';

function Badge({ children, tone }: { children: React.ReactNode; tone: 'gray' | 'green' | 'rose' | 'red' }) {
  const tones: Record<typeof tone, string> = {
    gray: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>
      {children}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{children}</div>
      <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

type UserDraft = {
  id?: string;
  name: string;
  email: string;
  username: string;
  password: string;
  role: string;
  status: string;
  title: string;
  description: string;
  service: string;
  occupation: string;
  phoneNo: string;
  country: string;
  address: string;
  profilePicture: string;
  profilePictureUrl: string;
  isProfileDisplay: boolean;
  performService: boolean;
};

const EMPTY_DRAFT: UserDraft = {
  name: '',
  email: '',
  username: '',
  password: '',
  role: 'admin',
  status: 'active',
  title: '',
  description: '',
  service: '',
  occupation: '',
  phoneNo: '',
  country: 'ID',
  address: '',
  profilePicture: '',
  profilePictureUrl: '',
  isProfileDisplay: false,
  performService: true,
};

const DEBOUNCE_MS = 400;

/** Quill leaves markup like `<p><br></p>` behind once a field has been typed in and cleared. */
function isBlankHtml(html: string): boolean {
  return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

export function UsersPage() {
  const { t } = useI18n();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<UserDraft>({ ...EMPTY_DRAFT });
  const [pendingImage, setPendingImage] = useState<PendingFile | null>(null);
  const [resetTarget, setResetTarget] = useState<UserRecord | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);
  const [resetPending, setResetPending] = useState<string | null>(null);
  const [resetCopied, setResetCopied] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadUsers = useCallback(async (params: { skip: number; limit: number; search: string }) => {
    setLoading(true);
    try {
      const result = await usersStore.fetch({
        skip: params.skip,
        limit: params.limit,
        search: params.search || undefined,
      });
      setUsers(result.records);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.loadFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadUsers({ skip, limit, search: query });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearchChange(value: string) {
    setQuery(value);
    setSkip(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadUsers({ skip: 0, limit, search: value });
    }, DEBOUNCE_MS);
  }

  function handlePageChange(next: { skip: number; limit: number }) {
    setSkip(next.skip);
    setLimit(next.limit);
    loadUsers({ skip: next.skip, limit: next.limit, search: query });
  }

  function refresh() {
    loadUsers({ skip, limit, search: query });
  }

  function openCreate() {
    setError(null);
    setDraft({ ...EMPTY_DRAFT });
    setPendingImage(null);
    setModalOpen(true);
  }

  function openEdit(user: UserRecord) {
    setError(null);
    setPendingImage(null);
    setDraft({
      id: user.id,
      name: user.name,
      email: user.email,
      username: user.username,
      password: '',
      role: user.role,
      status: user.status,
      title: user.title,
      description: user.description,
      service: user.service,
      occupation: user.occupation,
      phoneNo: user.phoneNo,
      country: user.country,
      address: user.address,
      profilePicture: user.profilePicture,
      profilePictureUrl: user.profilePictureUrl,
      isProfileDisplay: user.isProfileDisplay,
      performService: user.performService,
    });
    setModalOpen(true);
  }

  async function saveDraft() {
    setError(null);
    const name = draft.name.trim();
    const email = draft.email.trim().toLowerCase();
    const username = draft.username.trim().toLowerCase() || email;
    if (!name) return setError(t('users.validation.nameRequired'));
    if (!email || !email.includes('@')) return setError(t('users.validation.emailInvalid'));

    setSaving(true);
    try {
      let profilePicture: string | undefined = draft.profilePicture || undefined;
      if (pendingImage) {
        const { data: fileObj } = await filesApi.upload(pendingImage.name, pendingImage.base64);
        profilePicture = fileObj.fileId;
      }

      const payload: Record<string, unknown> = {
        name,
        email,
        username,
        role: draft.role,
        status: draft.status,
        title: draft.title.trim() || undefined,
        description: draft.description.trim() || undefined,
        service: isBlankHtml(draft.service) ? undefined : draft.service,
        occupation: draft.occupation.trim() || undefined,
        phoneNo: draft.phoneNo.trim(),
        country: draft.country.trim(),
        address: draft.address.trim() || undefined,
        profilePicture,
        isProfileDisplay: draft.isProfileDisplay,
        performService: draft.performService,
      };

      if (draft.id) {
        await usersStore.update(draft.id, payload);
      } else {
        if (!draft.password || draft.password.length < 6)
          return setError(t('users.validation.passwordMinLength'));
        await usersStore.create({ ...payload, password: draft.password } as any);
      }
      setModalOpen(false);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function removeUser(user: UserRecord) {
    const ok = window.confirm(t('users.confirmDelete', { name: user.name }));
    if (!ok) return;
    try {
      await usersStore.delete(user.id);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.deleteFailed'));
    }
  }

  /**
   * Mints a reset link for the user and copies it straight away — the modal keeps
   * the URL on screen so it can still be copied by hand when the clipboard is
   * unavailable (the async API needs a secure context).
   */
  async function generateResetLink(user: UserRecord) {
    setError(null);
    setResetPending(user.id);
    setResetCopied(false);
    try {
      const link = await usersStore.generateResetPasswordLink(user.id);
      if (!link) {
        setError(t('users.resetPassword.failed'));
        return;
      }
      setResetTarget(user);
      setResetLink(link);
      setResetCopied(await copyToClipboard(link));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.resetPassword.failed'));
    } finally {
      setResetPending(null);
    }
  }

  async function copyResetLink() {
    if (!resetLink) return;
    setResetCopied(await copyToClipboard(resetLink));
  }

  function closeResetModal() {
    setResetLink(null);
    setResetTarget(null);
    setResetCopied(false);
  }

  const roleTone = (role: string): 'red' | 'gray' => {
    const r = role.toLowerCase();
    return r === 'owner' || r === 'super admin' ? 'red' : 'gray';
  };

  const statusTone = (status: string): 'green' | 'rose' => {
    return status.toLowerCase() === 'active' ? 'green' : 'rose';
  };

  const roleLabel = (role: string) => t(`common.roles.${role.toLowerCase()}`) || role;
  const statusLabel = (status: string) => t(`common.status.${status.toLowerCase()}`) || status;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('users.title')}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {t('users.subtitle')}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button onClick={openCreate}>{t('users.addUser')}</Button>
        </div>
      </div>

      {error && !modalOpen ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-sm">
            <TextField
              label={t('common.search')}
              placeholder={t('users.searchPlaceholder')}
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t('common.total')} <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span>
          </div>
        </div>

        {/* Mobile card list */}
        <div className="mt-4 space-y-3 md:hidden">
          {loading && users.length === 0 ? (
            <DataCardEmpty>{t('users.loadingUsers')}</DataCardEmpty>
          ) : users.length === 0 ? (
            <DataCardEmpty>{t('common.noResults')}</DataCardEmpty>
          ) : (
            users.map((u) => (
              <DataCard key={u.id}>
                <div className="flex items-start gap-3">
                  {u.profilePictureUrl ? (
                    <img
                      src={u.profilePictureUrl}
                      alt={u.name}
                      className="h-10 w-10 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-100 text-sm font-bold text-red-600 dark:bg-red-950/40 dark:text-red-400">
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-slate-900 dark:text-slate-100">{u.name}</div>
                    {u.title && (
                      <div className="truncate text-xs text-slate-500 dark:text-slate-400">{u.title}</div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Badge tone={roleTone(u.role)}>{roleLabel(u.role)}</Badge>
                    <Badge tone={statusTone(u.status)}>{statusLabel(u.status)}</Badge>
                  </div>
                </div>
                <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                  <DataCardRow label={t('users.table.email')}>{u.email}</DataCardRow>
                  <DataCardRow label={t('users.table.phone')}>
                    {u.phoneNo || t('common.notAvailable')}
                  </DataCardRow>
                </div>
                <DataCardActions>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(u)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => generateResetLink(u)}
                    disabled={resetPending === u.id}
                  >
                    {resetPending === u.id
                      ? t('users.resetPassword.generating')
                      : t('users.resetPassword.action')}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => removeUser(u)}>
                    {t('common.delete')}
                  </Button>
                </DataCardActions>
              </DataCard>
            ))
          )}
        </div>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 dark:text-slate-400">
              <tr>
                <th className="py-2 pr-4 font-semibold">{t('users.table.name')}</th>
                <th className="py-2 pr-4 font-semibold">{t('users.table.email')}</th>
                <th className="py-2 pr-4 font-semibold">{t('users.table.phone')}</th>
                <th className="py-2 pr-4 font-semibold">{t('users.table.role')}</th>
                <th className="py-2 pr-4 font-semibold">{t('users.table.status')}</th>
                <th className="py-2 pr-0 font-semibold text-right">{t('users.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && users.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={6}>
                    {t('users.loadingUsers')}
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={6}>
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        {u.profilePictureUrl ? (
                          <img
                            src={u.profilePictureUrl}
                            alt={u.name}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="grid h-8 w-8 place-items-center rounded-full bg-red-100 text-xs font-bold text-red-600 dark:bg-red-950/40 dark:text-red-400">
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-slate-900 dark:text-slate-100">{u.name}</div>
                          {u.title && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">{u.title}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{u.email}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{u.phoneNo || t('common.notAvailable')}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={roleTone(u.role)}>{roleLabel(u.role)}</Badge>
                    </td>
                    <td className="py-3 pr-4">
                      <Badge tone={statusTone(u.status)}>{statusLabel(u.status)}</Badge>
                    </td>
                    <td className="py-3 pr-0">
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openEdit(u)}>
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => generateResetLink(u)}
                          disabled={resetPending === u.id}
                        >
                          {resetPending === u.id
                            ? t('users.resetPassword.generating')
                            : t('users.resetPassword.action')}
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => removeUser(u)}>
                          {t('common.delete')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination
            total={total}
            limit={limit}
            skip={skip}
            onChange={handlePageChange}
          />
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={draft.id ? t('users.editUser') : t('users.addUser')}
        onClose={() => (saving ? null : setModalOpen(false))}
        size="lg"
      >
        <div className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          ) : null}

          <SectionLabel>{t('users.sections.profilePicture')}</SectionLabel>

          <ImageUpload
            label={t('users.fields.photo')}
            currentUrl={draft.profilePictureUrl}
            pendingFile={pendingImage}
            onSelect={setPendingImage}
            onClear={() => setDraft((d) => ({ ...d, profilePicture: '', profilePictureUrl: '' }))}
          />

          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <label className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                checked={draft.isProfileDisplay}
                onChange={(e) => setDraft((d) => ({ ...d, isProfileDisplay: e.target.checked }))}
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('users.fields.profileDisplay')}</span>
            </label>
            <label className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                checked={draft.performService}
                onChange={(e) => setDraft((d) => ({ ...d, performService: e.target.checked }))}
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('users.fields.performService')}</span>
            </label>
          </div>

          <SectionLabel>{t('users.sections.account')}</SectionLabel>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('users.fields.name')}
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder={t('users.fields.namePlaceholder')}
            />
            <TextField
              label={t('users.fields.title')}
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder={t('users.fields.titlePlaceholder')}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('users.fields.username')}
              value={draft.username}
              onChange={(e) => setDraft((d) => ({ ...d, username: e.target.value }))}
              placeholder={t('users.fields.usernamePlaceholder')}
            />
            <TextField
              label={t('users.fields.email')}
              type="email"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              placeholder={t('users.fields.emailPlaceholder')}
            />
          </div>

          {!draft.id && (
            <TextField
              label={t('users.fields.password')}
              type="password"
              value={draft.password}
              onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))}
              placeholder={t('users.fields.passwordPlaceholder')}
            />
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('users.fields.role')}</div>
              <div className="flex flex-wrap gap-2">
                {['owner', 'admin', 'batamx', 'staff'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, role: opt }))}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition capitalize',
                      draft.role === opt
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                    )}
                  >
                    {t(`common.roles.${opt}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('users.fields.status')}</div>
              <div className="flex flex-wrap gap-2">
                {['active', 'inactive'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, status: opt }))}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition capitalize',
                      draft.status === opt
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                    )}
                  >
                    {t(`common.status.${opt}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <SectionLabel>{t('users.sections.contact')}</SectionLabel>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('users.fields.phone')}
              type="tel"
              value={draft.phoneNo}
              onChange={(e) => setDraft((d) => ({ ...d, phoneNo: e.target.value }))}
              placeholder={t('users.fields.phonePlaceholder')}
            />
            <TextField
              label={t('users.fields.country')}
              value={draft.country}
              onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}
              placeholder={t('users.fields.countryPlaceholder')}
            />
          </div>

          <TextField
            label={t('users.fields.address')}
            value={draft.address}
            onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
            placeholder={t('users.fields.addressPlaceholder')}
          />

          <SectionLabel>{t('users.sections.professional')}</SectionLabel>

          <TextField
            label={t('users.fields.occupation')}
            value={draft.occupation}
            onChange={(e) => setDraft((d) => ({ ...d, occupation: e.target.value }))}
            placeholder={t('users.fields.occupationPlaceholder')}
          />

          <RichTextEditor
            label={t('users.fields.service')}
            value={draft.service}
            onChange={(val) => setDraft((d) => ({ ...d, service: val }))}
            placeholder={t('users.fields.servicePlaceholder')}
          />

          <div>
            <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('users.fields.description')}</div>
            <textarea
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              rows={3}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder={t('users.fields.descriptionPlaceholder')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={saveDraft} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(resetLink)}
        title={t('users.resetPassword.title')}
        onClose={closeResetModal}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('users.resetPassword.description', { name: resetTarget?.name ?? '' })}
          </p>

          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              readOnly
              value={resetLink ?? ''}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            />
            <Button variant="secondary" onClick={copyResetLink} className="shrink-0">
              {t('users.resetPassword.copy')}
            </Button>
          </div>

          {resetCopied ? (
            <div className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
              {t('users.resetPassword.copied')}
            </div>
          ) : (
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {t('users.resetPassword.copyHint')}
            </div>
          )}

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300">
            {t('users.resetPassword.expiryNote')}
          </div>

          <div className="flex justify-end pt-1">
            <Button onClick={closeResetModal}>{t('common.close')}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
